import React, { ReactNode } from "react"
import { PROVIDER_TYPE } from "lib/types/common"

import * as Icons from "./providerIcons"
import { ReactComponent as BonusIcon } from "images/icons/hand-holding-dollar.svg"
import { ReactComponent as CardIcon } from "images/icons/gift-card.svg"
import { ReactComponent as BrinkIcon } from "images/brink-logo-icon.svg"

export const getProviderIcon = (provider: string) => {
  switch (provider) {
    case "KlarnaCheckout":
      return <Icons.KustomIcon />
    case "Adyen":
      return <Icons.AdyenIcon />
    case "SveaCheckout":
      return <Icons.SveaIcon />
    case "QliroOne":
      return <Icons.QliroIcon />
    case "WalleyCheckout":
      return <Icons.WalleyIcon />
    case "Avarda":
      return <Icons.AvardaIcon />
    case "Ledyer":
      return <Icons.LedyerIcon />
    case "CustomPayment":
      return <Icons.CustomPaymentIcon />
    case "TaxJar":
      return <Icons.TaxJarIcon />
    case "Ingrid":
      return <Icons.IngridIcon />
    case "Nshift":
      return <Icons.NShiftIcon />
    case "voyado":
      return <Icons.VoyadoIcon />
    case "Zaver":
      return <Icons.ZaverIcon />
    case "Retain24":
      return <Icons.Retain24Icon />
    case "BrinkZeroPayment":
      return <BrinkIcon />
    case "externalPrice":
      return <Icons.ExternalPriceIcon />
    default:
      return provider
  }
}

export const getProviderLogo = (provider: string) => {
  switch (provider) {
    case "KlarnaCheckout":
      return <Icons.KustomLogo />
    case "Adyen":
      return <Icons.AdyenLogo />
    case "SveaCheckout":
      return <Icons.SveaLogo />
    case "QliroOne":
      return <Icons.QliroLogo />
    case "WalleyCheckout":
      return <Icons.WalleyLogo />
    case "Avarda":
      return <Icons.AvardaLogo />
    case "Ledyer":
      return <Icons.LedyerLogo />
    case "TaxJar":
      return <Icons.TaxJarLogo />
    case "Ingrid":
      return <Icons.IngridLogo />
    case "Nshift":
      return <Icons.NShiftLogo />
    case "voyado":
      return <Icons.VoyadoLogo />
    case "Zaver":
      return <Icons.ZaverLogo />
    case "Retain24":
      return <Icons.Retain24Logo />
    default:
      return provider
  }
}

export type ProviderDetails = {
  type: string
  name: string
  desc: string
  icon: ReactNode
  slug: string
  statusUrl: string | undefined
  noEdit?: boolean
}

export type ProviderType = {
  [key: string]: ProviderDetails
}

export type ProvidersData = {
  [key: string]: ProviderDetails[]
}

export const providersData: ProvidersData = {
  Payment: [
    {
      type: PROVIDER_TYPE.KLARNA_CHECKOUT,
      name: "Kustom",
      desc: "A user-friendly online payment system allowing seamless transactions and various payment options for enhanced shopping experiences.",
      icon: getProviderIcon(PROVIDER_TYPE.KLARNA_CHECKOUT),
      slug: "/settings/providers/kustom",
      statusUrl: "https://status.kustom.co/"
    },
    {
      type: PROVIDER_TYPE.ADYEN,
      name: "Adyen",
      desc: "A global payment platform offering seamless transactions, robust security, and extensive payment methods worldwide.",
      icon: getProviderIcon(PROVIDER_TYPE.ADYEN),
      slug: "/settings/providers/adyen",
      statusUrl: "https://status.adyen.com/"
    },
    {
      type: PROVIDER_TYPE.SVEA_CHECKOUT,
      name: "Svea Checkout",
      desc: "A comprehensive online payment platform providing seamless transactions and diverse payment options for customers in the Nordics.",
      icon: getProviderIcon(PROVIDER_TYPE.SVEA_CHECKOUT),
      slug: "/settings/providers/svea",
      statusUrl: "https://status.svea.com/"
    },
    {
      type: PROVIDER_TYPE.WALLEY_CHECKOUT,
      name: "Walley Checkout",
      desc: "An efficient online payment system facilitating secure transactions and diverse payment methods for seamless customer experiences.",
      icon: getProviderIcon(PROVIDER_TYPE.WALLEY_CHECKOUT),
      slug: "/settings/providers/walley",
      statusUrl: "https://status.walleypay.com/"
    },
    {
      type: PROVIDER_TYPE.QLIRO_ONE,
      name: "Qliro One",
      desc: "A unified online payment platform offering streamlined transactions and multiple payment options for enhanced customer convenience.",
      icon: getProviderIcon(PROVIDER_TYPE.QLIRO_ONE),
      slug: "/settings/providers/qliro",
      statusUrl: "https://status.qliro.com/"
    },
    {
      type: PROVIDER_TYPE.AVARDA,
      name: "Avarda",
      desc: "Avarda offers true white-label payment solutions and sales financing for e-com merchants in the Nordics.",
      icon: getProviderIcon(PROVIDER_TYPE.AVARDA),
      slug: "/settings/providers/avarda",
      statusUrl: "https://docs.avarda.com/"
    },
    {
      type: PROVIDER_TYPE.ZAVER,
      name: "Zaver",
      desc: "Zaver offers flexible payment options that can handle carts of all sizes, big and small.",
      icon: getProviderIcon(PROVIDER_TYPE.ZAVER),
      slug: "/settings/providers/zaver",
      statusUrl: "https://api-docs.zaver.se/"
    },
    {
      type: PROVIDER_TYPE.LEDYER,
      name: "Ledyer",
      desc: "Ledyer offers flexible payment options to businesses, organizations, and public entities with a smooth and simple B2B payment solution.",
      icon: getProviderIcon(PROVIDER_TYPE.LEDYER),
      slug: "/settings/providers/ledyer",
      statusUrl: "https://ledyer.com/"
    },
    {
      type: PROVIDER_TYPE.CUSTOM_PAYMENT,
      name: "Custom Payment",
      desc: "Custom payment provider integration with configurable authentication and base URLs for flexible payment processing.",
      icon: getProviderIcon(PROVIDER_TYPE.CUSTOM_PAYMENT),
      slug: "/settings/providers/custom-payment",
      statusUrl: undefined
    }
  ],
  Shipping: [
    {
      type: PROVIDER_TYPE.NSHIFT,
      name: "nShift",
      desc: "A platform that optimizes and manages the delivery process, ensuring efficient and timely shipment of goods to customers.",
      icon: getProviderIcon(PROVIDER_TYPE.NSHIFT),
      slug: "/settings/providers/Nshift",
      statusUrl: "https://status.nshiftportal.com/"
    },
    {
      type: PROVIDER_TYPE.INGRID,
      name: "Ingrid",
      desc: "An efficient logistics solution, streamlining delivery processes for businesses to ensure timely and reliable shipments, optimizing overall operations.",
      icon: getProviderIcon(PROVIDER_TYPE.INGRID),
      slug: "/settings/providers/ingrid",
      statusUrl: "http://status.ingrid.com/"
    }
  ],
  CRM: [
    {
      type: PROVIDER_TYPE.VOYADO,
      name: "Voyado",
      desc: "A customer retention platform offering personalized marketing strategies and targeted promotions to enhance customer loyalty and drive sales growth for businesses.",
      icon: getProviderIcon(PROVIDER_TYPE.VOYADO),
      slug: "/settings/providers/voyado",
      statusUrl: "https://status.voyado.com/"
    }
  ],
  ["Gift card"]: [
    {
      type: PROVIDER_TYPE.RETAIN24,
      name: "Retain24",
      desc: " A versatile and customizable digital or physical voucher facilitating purchases at affiliated businesses, enhancing customer engagement and loyalty.",
      icon: getProviderIcon(PROVIDER_TYPE.RETAIN24,),
      slug: "/settings/providers/retain24",
      statusUrl: "https://www.retain24status.com/retain24status/"
    },
    {
      type: PROVIDER_TYPE.BSG_GIFT_CARD,
      name: "Brink Custom Gift Card",
      desc: "Custom Gift card provider.",
      icon: <CardIcon />,
      slug: "/settings/providers/bsg-gift-card",
      statusUrl: undefined
    },
    {
      type: PROVIDER_TYPE.KBS_GIFT_CARD,
      name: "KBS - Custom",
      desc: "Custom Gift card provider for Kids Brand Store.",
      icon: <CardIcon />,
      slug: "",
      statusUrl: undefined,
      noEdit: true
    }
  ],
  Bonus: [
    {
      type: PROVIDER_TYPE.BONUS,
      name: "Custom bonus provider",
      desc: "Setup a custom bonus provvider together with Brink.",
      icon: <BonusIcon />,
      slug: "/settings/providers/bonus",
      statusUrl: undefined
    }
  ],
  ["External Price"]: [
    {
      type: PROVIDER_TYPE.EXTERNAL_PRICE,
      name: "Custom external price provider",
      desc: "Setup a custom external price provider together with Brink.",
      icon: getProviderIcon(PROVIDER_TYPE.EXTERNAL_PRICE),
      slug: "/settings/providers/external-price",
      statusUrl: undefined
    }
  ],
  Tax: [
    {
      type: PROVIDER_TYPE.TAX_JAR,
      name: "TaxJar",
      desc: "Automated sales tax calculation and compliance solution for e-commerce businesses across the United States.",
      icon: getProviderIcon(PROVIDER_TYPE.TAX_JAR),
      slug: "/settings/providers/tax-jar",
      statusUrl: undefined
    }
  ]
}
